<template>
	<div class="mention-list">
		<template v-if="loading">
			<SuiSkeleton width="15rem" height="10rem" />
		</template>
		<template v-if="!loading && filteredUsers.length">
			<button
				v-for="(user, index) in filteredUsers"
				:key="user.id"
				ref="$buttons"
				type="button"
				class="mention-list__item"
				:class="{
					'mention-list__item--selected': index === selectedIndex,
				}"
				@click="selectItem(index)"
			>
				<SuiAvatar
					:url="user.profilePicture?.mini"
					:initials="user.initials || ''"
					:color="user?.hslColor || ''"
					size="xx-small"
				/>
				<span>{{ user.name }}</span>
			</button>
		</template>
		<div
			v-if="!loading && filteredUsers.length === 0"
			class="mention-list__item"
		>
			No result
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, computed, nextTick } from 'vue';
import type { Editor } from '@tiptap/vue-3';
import { useMentionListQuery } from '@/generated/graphql';
import { SuiSkeleton, SuiAvatar } from '@developers/scoro-ui';
import type { KeyDownProps } from '@/features/App/AppTextEditor/extensions/Emoji/types';
import { KeyboardKey } from '@/constants/constants';

interface MentionListProps {
	query: string;
	command: (payload: { id: string; label: string }) => void;
	editor: Editor;
}

const props = defineProps<MentionListProps>();

const { result, loading } = useMentionListQuery({});

const allUsers = computed(() => result.value?.UsersList || []);
const filteredUsers = computed(() =>
	allUsers.value.filter((user) =>
		user.name.toLowerCase().includes(props.query.toLowerCase())
	)
);

const selectedIndex = ref(0);
const $buttons = ref<HTMLButtonElement[]>([]);

watch(
	() => props.query,
	() => {
		selectedIndex.value = 0;
	},
	{ deep: true }
);

const selectItem = (index: number) => {
	const item = filteredUsers.value[index];
	if (item) {
		props.command({ id: item.id, label: item.name });
	}
};

const setFocus = (index: number) => {
	nextTick(() => {
		if ($buttons.value[index]) {
			$buttons.value[index].scrollIntoView({
				behavior: 'auto',
				block: 'nearest',
			});
		}
	});
};

const upHandler = () => {
	selectedIndex.value =
		(selectedIndex.value + filteredUsers.value.length - 1) %
		filteredUsers.value.length;
	setFocus(selectedIndex.value);
};

const downHandler = () => {
	selectedIndex.value =
		(selectedIndex.value + 1) % filteredUsers.value.length;
	setFocus(selectedIndex.value);
};

const enterHandler = () => {
	selectItem(selectedIndex.value);
};

const onKeyDown = (props: KeyDownProps) => {
	switch (props.event.key) {
		case KeyboardKey.ARROW_UP:
		case KeyboardKey.ARROW_LEFT:
			upHandler();
			return true;
		case KeyboardKey.ARROW_DOWN:
		case KeyboardKey.ARROW_RIGHT:
			downHandler();
			return true;
		case KeyboardKey.ENTER:
			enterHandler();
			return true;
		default:
			return false;
	}
};

defineExpose({ onKeyDown });
</script>

<style lang="scss">
.mention-list {
	background: $neutral-00;
	border-radius: 0.5rem;
	box-shadow: $scoro-box-shadow;
	display: flex;
	flex-direction: column;
	overflow: auto;
	padding: 0;
	position: relative;
	max-height: 30rem;

	&__item {
		align-items: center;
		display: flex;
		gap: 1rem;
		width: 100%;
		padding: 1rem;

		&:hover,
		&--selected {
			background-color: $neutral-20;
		}
	}
}
</style>
